import React, { useState, useEffect, useRef } from 'react';
import { User } from '../types/User';
import { X, Send, Calendar, MapPin, Building, Mail, ChevronDown } from 'lucide-react';
import MeetingScheduleModal from './MeetingScheduleModal';
import { MessageService, Message } from '../services/messageService';

interface ChatInterfaceProps {
  user: User;
  currentUser: User;
  onClose: () => void;
  onMessageSent?: () => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ user, currentUser, onClose, onMessageSent }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [showAbout, setShowAbout] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load existing messages from database
  useEffect(() => {
    loadConversation();
  }, [user.id, currentUser.id]);

  const loadConversation = async () => {
    setLoading(true);
    const conversation = await MessageService.getConversation(currentUser.id, user.id);
    setMessages(conversation);
    
    // Mark messages as read
    await MessageService.markMessagesAsRead(currentUser.id, user.id);
    
    setLoading(false);
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    // Send message to database
    MessageService.sendMessage(currentUser.id, user.id, newMessage.trim())
      .then(({ success, error }) => {
        if (success) {
          setNewMessage('');
          // Reload conversation to show new message
          loadConversation();
          // Notify parent component
          onMessageSent?.();
        } else {
          console.error('Failed to send message:', error);
        }
      });
  };

  const handleScheduleMeeting = () => {
    setIsScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setIsScheduleModalOpen(false);
  };

  return (
    <div className="w-full lg:w-80 bg-white shadow-lg border-l flex flex-col h-dvh lg:h-full fixed lg:relative inset-0 lg:inset-auto z-40 lg:z-auto">
      {/* Header */}
      <div className="p-4 border-b bg-gray-50 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3 min-w-0">
            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
              {user.avatar ? (
                <img 
                  src={user.avatar} 
                  alt={user.name}
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                <span className="text-sm font-semibold text-gray-600">
                  {user.name.split(' ').map(n => n[0]).join('')}
                </span>
              )}
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-800 truncate">{user.name}</h3>
              <p className="text-sm text-gray-600 truncate">{user.position}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded lg:block"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* About Section Toggle */}
        <button
          onClick={() => setShowAbout(!showAbout)}
          className="flex items-center justify-between w-full text-left p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <span className="text-sm font-medium text-gray-700">About</span>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-500">View</span>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showAbout ? 'rotate-180' : ''}`} />
          </div>
        </button>

        {/* About Content */}
        {showAbout && (
          <div className="mt-3 p-3 bg-white rounded-lg border">
            <div className="space-y-2 text-sm">
              <div className="flex items-center text-gray-600">
                <Building className="w-4 h-4 mr-2" />
                <span>{user.company}</span>
              </div>
              <div className="flex items-center text-gray-600">
                <Mail className="w-4 h-4 mr-2" />
                <span>{user.email}</span>
              </div>
              <div className="flex items-center text-gray-600">
                <MapPin className="w-4 h-4 mr-2" />
                <span>{user.country}</span>
              </div>
            </div>
            
            {user.about && (
              <div className="mt-3 pt-3 border-t">
                <p className="text-sm text-gray-600">{user.about}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-4 space-y-2">
              <button
                onClick={handleScheduleMeeting}
                className="w-full flex items-center justify-center space-x-2 px-3 py-2 border rounded-md hover:bg-yellow-50 transition-colors"
                style={{ borderColor: '#C89116', color: '#C89116' }}
              >
                <Calendar className="w-4 h-4" />
                <span>Schedule Meeting</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
        {loading ? (
          <div className="text-center text-gray-500 text-sm mt-8">
            <p>Loading conversation...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-500 text-sm mt-8">
            <p>Enter a message to start conversation</p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.sender_id === currentUser.id ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-xs px-4 py-2 rounded-lg ${
                  message.sender_id === currentUser.id
                    ? 'text-white'
                    : 'bg-gray-100 text-gray-800'
                }`}
                style={message.sender_id === currentUser.id ? { backgroundColor: '#C89116' } : {}}
              >
                <p className="text-sm">{message.content}</p>
                <p className={`text-xs mt-1 ${
                  message.sender_id === currentUser.id ? 'text-yellow-100' : 'text-gray-500'
                }`}>
                  {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="p-4 border-t bg-gray-50 flex-shrink-0">
        <form onSubmit={handleSendMessage} className="flex space-x-2">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-600 text-sm"
          />
          <button
            type="submit"
            disabled={!newMessage.trim()}
            className="p-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            style={{ backgroundColor: '#C89116' }}
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>

      {/* Meeting Schedule Modal */}
      {isScheduleModalOpen && (
        <MeetingScheduleModal
          participant={user}
          currentUser={currentUser}
          onClose={handleCloseScheduleModal}
        />
      )}
    </div>
  );
};

export default ChatInterface;